import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner, faPlus, faBox } from '@fortawesome/free-solid-svg-icons';
import { crmService } from '../services/crmService';
import { useFlashMessage } from './FlashMessage';
import SearchableSelect from './Invoicing/SearchableSelect';
import axiosInstance from '../utils/axiosConfig';
import { COUNTRY_OPTIONS } from '../utils/constants';
import {
    CATEGORIES,
    SUBTYPE_LABEL_MAX_LENGTH,
    getCategoryForSubtype,
    getSubtypesForCategory,
    normalizeSubtypeLabel,
    validateSubtypeLabel,
    isBrandedCategory,
} from '../data/productTaxonomy';
import { costCentreLabel } from '../utils/costCentreLabel';

const StockItemForm = ({
    isOpen,
    onClose,
    onSuccess,
    initialCategory,
    initialProductType,
    preFilledData,
    analysisId,
    // "You may already have this product". When the caller can do something
    // useful with an existing product (hand it back to the deal line it was
    // opened from, or open its item step), it passes onUseExisting and the
    // warning block offers "Use this one instead" per match. Callers that
    // cannot -- none today, but the prop is optional on purpose -- still get
    // the warning, just without the shortcut.
    onUseExisting,
    // A product is created together with its first item, in ONE atomic POST
    // (invoice-product-search-silent-drops, 2026-08-19): this form no longer
    // writes anything on its own, it hands the validated payload up as
    // onSuccess({ draft }) and the item step submits both. Writing the product
    // here is what left "0 variants" products that the Products module listed
    // and no invoice / delivery / recap picker could ever find.
    deferSubmit = false,
}) => {
    const { showSuccess, showError } = useFlashMessage();

    const resolvedInitialCategory =
        preFilledData?.category
        || getCategoryForSubtype(preFilledData?.product_type)
        || initialCategory
        || getCategoryForSubtype(initialProductType)
        || 'coal';

    const [formData, setFormData] = useState({
        category: resolvedInitialCategory,
        product_type: preFilledData?.product_type || initialProductType || '',
        subtype_label: preFilledData?.subtype_label || '',
        supplier_brand: preFilledData?.supplier_brand || '',
        product_name: preFilledData?.product_name || '',
        product_code: preFilledData?.product_code || '',
        product_description: preFilledData?.product_description || '',
        country_of_origin: preFilledData?.country_of_origin || '',
        supplier_crm_profile: '',
        source_location: preFilledData?.source_location || '',
        // Per-leg finance coding (required on create; group-chart numbers/codes)
        gl_account_number_sales: '',
        gl_account_number_purchase: '',
        cost_center_code_sales: '',
        cost_center_code_purchase: '',
        cost_category_sales: '',
        cost_category_purchase: '',
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // "You may already have this product."
    //
    // status is deliberately four-valued. 'failed' is NOT 'clear': a check that
    // could not run must never read as "we looked and there was nothing", or
    // the trader creates the duplicate believing HMS approved it.
    //   idle    — not asked yet (too little typed, or nothing changed)
    //   checking— in flight
    //   clear   — asked, nothing matched
    //   matched — asked, these exist already
    //   failed  — could not ask
    const [duplicateCheck, setDuplicateCheck] = useState({ status: 'idle', matches: [] });
    // Set once the user has seen a warning and chosen to create anyway. The
    // check is advisory: it informs, it never blocks.
    const [overrideDuplicates, setOverrideDuplicates] = useState(false);

    // CRM Autocomplete state
    const [crmPartners, setCrmPartners] = useState([]); // Array of {id, name} objects
    const [filteredCrmPartners, setFilteredCrmPartners] = useState([]);
    const [loadingCrmPartners, setLoadingCrmPartners] = useState(false);
    const [showCrmDropdown, setShowCrmDropdown] = useState(false);
    const [crmSearchTerm, setCrmSearchTerm] = useState('');
    const [debouncedCrmSearchTerm, setDebouncedCrmSearchTerm] = useState('');
    const [selectedCrmPartnerName, setSelectedCrmPartnerName] = useState(''); // For display
    const [creatingProfile, setCreatingProfile] = useState(false);
    const crmDropdownRef = useRef(null);
    const crmSearchInputRef = useRef(null);

    const countries = COUNTRY_OPTIONS;

    const subtypeOptions = getSubtypesForCategory(formData.category);
    const isOtherCategory = formData.category === 'other';
    // Branded products (Lubrication) are identified by brand + product name.
    // Their subtype, origin and supplier questions are not just optional, they
    // are meaningless -- a drum of Shell Alexia has no mine and no country.
    const isBranded = isBrandedCategory(formData.category);

    // The fields the duplicate check is built from. Editing any of them makes a
    // previous answer stale, so it is thrown away rather than left on screen.
    const IDENTITY_FIELDS = [
        'category', 'product_type', 'subtype_label',
        'supplier_brand', 'product_name', 'product_code',
        'country_of_origin', 'source_location', 'supplier_crm_profile',
    ];

    const duplicatePayload = (data) => ({
        category: data.category || '',
        product_type: data.product_type || '',
        subtype_label: data.subtype_label || '',
        supplier_brand: data.supplier_brand || '',
        product_name: data.product_name || '',
        product_code: data.product_code || '',
        // A bulk commodity is not identified by its subtype alone -- origin,
        // source location and supplier are part of what makes it that product,
        // and the backend refuses to answer without at least one of them.
        country_of_origin: data.country_of_origin || '',
        source_location: data.source_location || '',
        supplier_crm_profile: data.supplier_crm_profile || null,
    });

    // Mirrors has_enough_to_check() in backend/stock/services/product_duplicates.py.
    // Asking early is only a wasted round trip; the backend refuses to answer on
    // half-typed input either way, so the two must agree on what "enough" is.
    const hasEnoughToCheck = (data) => {
        const t = (v) => String(v || '').trim();
        if (!t(data.category)) return false;
        if (isBrandedCategory(data.category)) {
            return (!!t(data.supplier_brand) && !!t(data.product_name)) || !!t(data.product_code);
        }
        if (!t(data.product_type)) return false;
        if (data.category === 'other' && !t(data.subtype_label)) return false;
        return !!t(data.country_of_origin)
            || !!t(data.source_location)
            || !!data.supplier_crm_profile;
    };

    const resetDuplicateCheck = () => {
        setDuplicateCheck({ status: 'idle', matches: [] });
        setOverrideDuplicates(false);
    };

    /**
     * Ask whether this product already exists. Returns the result rather than
     * only setting state, so the submit path can act on a check it just ran.
     *
     * NEVER throws: a failed check is reported as 'failed' and the caller
     * carries on. This must not be the thing that stops a product being created.
     */
    const runDuplicateCheck = async (data = formData) => {
        const payload = duplicatePayload(data);
        if (!hasEnoughToCheck(payload)) {
            const idle = { status: 'idle', matches: [] };
            setDuplicateCheck(idle);
            return idle;
        }
        setDuplicateCheck({ status: 'checking', matches: [] });
        try {
            const response = await axiosInstance.post(
                '/api/stock/stock-items/check-duplicate/', payload,
            );
            const matches = Array.isArray(response.data?.matches) ? response.data.matches : [];
            // ``checked: false`` means the backend declined to answer. That is
            // not an all-clear either.
            const result = response.data?.checked === false
                ? { status: 'idle', matches: [] }
                : { status: matches.length ? 'matched' : 'clear', matches };
            setDuplicateCheck(result);
            return result;
        } catch (error) {
            console.error('Duplicate product check failed:', error);
            const failed = { status: 'failed', matches: [] };
            setDuplicateCheck(failed);
            return failed;
        }
    };

    const handleIdentityBlur = () => {
        if (duplicateCheck.status === 'checking') return;
        runDuplicateCheck();
    };

    useEffect(() => {
        if (preFilledData) {
            const inferredCategory =
                preFilledData.category
                || getCategoryForSubtype(preFilledData.product_type)
                || resolvedInitialCategory;
            setFormData((prev) => ({
                ...prev,
                category: inferredCategory,
                product_type: preFilledData.product_type || prev.product_type,
                subtype_label: preFilledData.subtype_label || prev.subtype_label,
                country_of_origin: preFilledData.country_of_origin || prev.country_of_origin,
                source_location: preFilledData.source_location || prev.source_location,
            }));

            if (preFilledData.supplier) {
                setSelectedCrmPartnerName(preFilledData.supplier);
                setCrmSearchTerm(preFilledData.supplier);
            }
        }
    }, [preFilledData, resolvedInitialCategory]);

    // Handle outside clicks for CRM dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (crmDropdownRef.current && !crmDropdownRef.current.contains(event.target)) {
                setShowCrmDropdown(false);
                setCrmSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Debounce CRM search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedCrmSearchTerm(crmSearchTerm);
        }, 300);

        return () => clearTimeout(timer);
    }, [crmSearchTerm]);

    // Reset creating profile state when search term is cleared
    useEffect(() => {
        if (creatingProfile && !crmSearchTerm.trim()) {
            setCreatingProfile(false);
        }
    }, [crmSearchTerm, creatingProfile]);

    // Filter CRM partners based on search term
    useEffect(() => {
        if (!debouncedCrmSearchTerm.trim()) {
            setFilteredCrmPartners([]);
        } else {
            const filtered = crmPartners.filter(partner =>
                partner.name.toLowerCase().includes(debouncedCrmSearchTerm.toLowerCase())
            );
            setFilteredCrmPartners(filtered);
        }
    }, [debouncedCrmSearchTerm, crmPartners]);

    // Fetch CRM partner data on component mount
    useEffect(() => {
        const fetchCrmPartners = async () => {
            try {
                setLoadingCrmPartners(true);
                // Use the getPartnerData method I added earlier
                const partnerData = await crmService.getPartnerData();
                
                // Sort alphabetically by name
                partnerData.sort((a, b) => a.name.localeCompare(b.name));
                
                setCrmPartners(partnerData);
                console.log(`Loaded ${partnerData.length} CRM partners for stock supplier dropdown`);
            } catch (error) {
                console.error('Error fetching CRM partners for stock supplier:', error);
                setCrmPartners([]);
            } finally {
                setLoadingCrmPartners(false);
            }
        };

        fetchCrmPartners();
    }, []);

    const handleAccountingChange = (name) => (value) => {
        setFormData((prev) => ({ ...prev, [name]: value || '' }));
        // Required pickers must drop their error the moment a value is chosen.
        setErrors((prev) => (prev[name] ? { ...prev, [name]: '' } : prev));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // A warning about what was typed a moment ago is worse than no warning.
        if (IDENTITY_FIELDS.includes(name)) {
            resetDuplicateCheck();
        }
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCategoryChange = (e) => {
        const newCategory = e.target.value;
        setFormData((prev) => {
            const next = { ...prev, category: newCategory };
            if (newCategory === 'other') {
                next.product_type = 'other';
            } else {
                const stillValid = getSubtypesForCategory(newCategory).some(
                    (s) => s.value === prev.product_type,
                );
                next.product_type = stillValid ? prev.product_type : '';
                next.subtype_label = '';
            }
            return next;
        });
        setErrors((prev) => ({ ...prev, category: '', product_type: '', subtype_label: '' }));
        resetDuplicateCheck();
    };

    const handleCrmPartnerSelect = (partner) => {
        // Set the ID for submission to backend
        setFormData(prev => ({
            ...prev,
            supplier_crm_profile: partner.id
        }));
        // Set the name for display
        setSelectedCrmPartnerName(partner.name);
        setShowCrmDropdown(false);
        setCrmSearchTerm('');
        // The supplier is part of a bulk product's identity: an answer given
        // before it was picked was about a different product, and picking it is
        // often the last thing typed, so ask again with it.
        resetDuplicateCheck();
        runDuplicateCheck({ ...formData, supplier_crm_profile: partner.id });

        // Clear error when user selects a partner
        if (errors.supplier_crm_profile) {
            setErrors(prev => ({
                ...prev,
                supplier_crm_profile: ''
            }));
        }
    };

    const handleCreateCrmProfile = async () => {
        if (!debouncedCrmSearchTerm.trim()) {
            showError('Please enter a company name to create a profile');
            return;
        }

        try {
            setCreatingProfile(true);
            
            // Create minimal CRM profile with just the legal entity name
            const newProfileData = {
                legal_entity_name: debouncedCrmSearchTerm.trim(),
                // Set some basic defaults for better UX
                creditor_debtor_selections: ['Supplier'],
                partner_type_selections: ['Supplier'],
                industry_selections: ['Commodities Trading'],
                kyc_status_current: 'Pending',
                aml_risk_level_current: 'Medium',
                overall_risk_rating_score: 5
            };

            console.log('Creating new CRM profile:', newProfileData);
            const newProfile = await crmService.createProfile(newProfileData);
            
            // Success - update the autocomplete list and select the new profile
            const updatedPartners = [...crmPartners, { id: newProfile.id, name: newProfile.legal_entity_name }];
            setCrmPartners(updatedPartners);
            
            // Auto-select the newly created profile
            handleCrmPartnerSelect({ id: newProfile.id, name: newProfile.legal_entity_name });
            
            showSuccess(`Successfully created CRM profile for "${newProfile.legal_entity_name}"`);
            
        } catch (error) {
            console.error('Error creating CRM profile:', error);
            
            // Handle specific error cases
            if (error.response?.status === 400) {
                const errorData = error.response.data;
                
                // Check for uniqueness constraint violation
                if (errorData.legal_entity_name && 
                    errorData.legal_entity_name[0]?.includes('already exists')) {
                    showError(`A partner with the name "${debouncedCrmSearchTerm}" already exists in the CRM system`);
                } else if (errorData.legal_entity_name) {
                    showError(`Validation error: ${errorData.legal_entity_name[0]}`);
                } else {
                    showError('Invalid profile data. Please check the company name and try again.');
                }
            } else if (error.response?.status === 401) {
                showError('You are not authorized to create CRM profiles. Please contact your administrator.');
            } else if (error.response?.status === 500) {
                showError('Server error while creating profile. Please try again later.');
            } else {
                showError('Failed to create CRM profile. Please try again or contact support.');
            }
        } finally {
            setCreatingProfile(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.category) {
            newErrors.category = 'Category is required';
        }

        // For the 'other' category the subtype dropdown is replaced by the
        // free-text subtype_label field; product_type is implicitly 'other'.
        // Branded categories show no subtype selector at all and the backend
        // fills it in, so asking here would block a field nobody was shown.
        if (formData.category !== 'other' && !isBranded && !formData.product_type) {
            newErrors.product_type = 'Product type is required';
        }

        if (formData.category === 'other') {
            const labelError = validateSubtypeLabel(formData.subtype_label);
            if (labelError) newErrors.subtype_label = labelError;
        }

        if (isBranded) {
            // Brand + product name together ARE the product's identity: the backend
            // builds its unique key from exactly these two. Blank either and every
            // lube product collapses onto one row, so this refuses here as well as
            // there rather than letting the user find out on save.
            if (!String(formData.supplier_brand || '').trim()) {
                newErrors.supplier_brand = 'Supplier brand is required';
            }
            if (!String(formData.product_name || '').trim()) {
                newErrors.product_name = 'Product name is required';
            }
        } else {
            const hasSupplier = formData.supplier_crm_profile || (selectedCrmPartnerName && selectedCrmPartnerName.trim());
            const hasSourceLocation = formData.source_location && formData.source_location.trim();

            if (!hasSupplier && !hasSourceLocation) {
                newErrors.supplier_crm_profile = 'Either supplier or source location is required';
                newErrors.source_location = 'Either supplier or source location is required';
            }
        }

        // Finance coding: only the two GL accounts are required for new products
        // (mirrors the backend gate; existing products are grandfathered there).
        // Cost centre and cost category are reporting dimensions that can be
        // filled in later -- demanding all six up front blocked product creation
        // for anyone who did not yet know how to classify the cost side.
        //
        // Lubrication is exempt (Craig, 2026-09-03): the range arrives as a ~900-row
        // supplier catalogue with no GL coding and finance codes it afterwards.
        // Category-wide on purpose -- gating this form while the bulk importer walks
        // past it would make the same product legal or illegal depending on how it
        // was created. The Products tab shows a "needs GL coding" count so the gap
        // stays visible.
        const codingFields = isBranded ? {} : {
            gl_account_number_sales: 'GL account (sales) is required',
            gl_account_number_purchase: 'GL account (purchase) is required',
        };
        Object.entries(codingFields).forEach(([field, message]) => {
            if (!String(formData[field] || '').trim()) {
                newErrors[field] = message;
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        // Last chance to say "you may already have this" -- the row must not be
        // written first and the warning shown after. The check only PAUSES the
        // first submit: a second click (the "Create it anyway" path) sets
        // overrideDuplicates and goes through regardless, and a check that fails
        // or finds nothing never pauses anything.
        if (!overrideDuplicates) {
            const result = duplicateCheck.status === 'matched'
                ? duplicateCheck
                : await runDuplicateCheck();
            if (result.status === 'matched') {
                setOverrideDuplicates(true);
                return;
            }
        }

        setLoading(true);
        
        try {
            let submitData = { ...formData };

            if (submitData.category === 'other') {
                submitData.product_type = 'other';
                submitData.subtype_label = normalizeSubtypeLabel(submitData.subtype_label);
            } else {
                delete submitData.subtype_label;
            }

            // Finance coding fields are required and validated above — send as-is.

            if (!formData.supplier_crm_profile && selectedCrmPartnerName && selectedCrmPartnerName.trim()) {
                console.log('🏢 Creating CRM profile for supplier:', selectedCrmPartnerName);
                try {
                    const crmResponse = await crmService.createProfile({
                        legal_entity_name: selectedCrmPartnerName.trim()
                    });
                    
                    console.log('✅ CRM profile created:', crmResponse);
                    submitData.supplier_crm_profile = crmResponse.id;
                    showSuccess(`CRM profile created for ${selectedCrmPartnerName}`);
                } catch (crmError) {
                    console.error('❌ Failed to create CRM profile:', crmError);
                    
                    // Check if this is a duplicate profile error
                    if (crmError.message && crmError.message.includes('already exists')) {
                        console.log('🔍 CRM profile already exists, searching for existing profile...');
                        try {
                            // Search for existing profile by name
                            const existingProfiles = await crmService.getProfiles({ 
                                search: selectedCrmPartnerName.trim() 
                            });
                            
                            // Find exact match
                            const exactMatch = existingProfiles.results ? 
                                existingProfiles.results.find(p => p.legal_entity_name === selectedCrmPartnerName.trim()) :
                                existingProfiles.find(p => p.legal_entity_name === selectedCrmPartnerName.trim());
                            
                            if (exactMatch) {
                                console.log('✅ Found existing CRM profile:', exactMatch);
                                submitData.supplier_crm_profile = exactMatch.id;
                                showSuccess(`Using existing CRM profile for ${selectedCrmPartnerName}`);
                            } else {
                                console.log('⚠️ Could not find existing profile, proceeding without supplier');
                                showError(`Could not find or create CRM profile for ${selectedCrmPartnerName}. Proceeding without it.`);
                            }
                        } catch (searchError) {
                            console.error('❌ Failed to search for existing CRM profile:', searchError);
                            showError(`Could not find existing CRM profile for ${selectedCrmPartnerName}. Proceeding without it.`);
                        }
                    } else {
                        // Other CRM creation errors
                        showError(`Could not create CRM profile for ${selectedCrmPartnerName}. Proceeding without it.`);
                    }
                }
            }
            
            if (deferSubmit) {
                // Nothing is written yet — the item step POSTs product + item together.
                onSuccess({ draft: submitData, product_type: submitData.product_type });
                return;
            }

            console.log('Creating stock item with data:', submitData);
            const response = await axiosInstance.post('/api/stock/stock-items/', submitData);
            
            console.log('Stock item response:', response.data);
            
            // Check if this is an existing item (duplicate handling)
            if (response.data.is_existing) {
                console.log('✅ Existing stock item found, proceeding to variant creation');
                showSuccess(response.data.message || 'Stock item already exists. You can now add variants to this existing item.');
            } else {
                console.log('✅ New stock item created successfully');
                showSuccess('Stock item created successfully');
            }
            
            onSuccess(response.data);
            // Don't call handleClose() on successful submission - let parent handle it
        } catch (error) {
            console.error('Error creating stock item:', error);
            
            // Handle different error types
            if (error.response?.status === 400) {
                // Validation errors
                const errorData = error.response.data;
                console.error('Validation errors:', errorData);
                setErrors(errorData);
            } else if (error.response?.status === 401) {
                setErrors({ general: 'You are not authorized to create stock items. Please log in again.' });
            } else if (error.response?.status === 404) {
                setErrors({ general: 'Stock API endpoint not found. Please contact support.' });
            } else if (error.response?.status >= 500) {
                setErrors({ general: 'Server error. Please try again later.' });
            } else {
                setErrors({ general: 'Failed to create stock item. Please try again.' });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = async () => {
        // If we have an analysis ID, discard it since user is canceling
        if (analysisId) {
            try {
                console.log('🗑️ Auto-discarding contract analysis on stock item form close:', analysisId);
                showSuccess('Discarding contract analysis...');
                await axiosInstance.delete(`/api/stock/contracts/discard/${analysisId}/`);
                console.log('✅ Contract analysis auto-discarded on stock item form close');
                showSuccess('Contract analysis discarded successfully');
            } catch (error) {
                console.error('❌ Failed to auto-discard contract analysis:', error);
                // Handle different error types
                if (error.response?.status === 404) {
                    console.log('ℹ️ Analysis already deleted or not found');
                    // Continue with close anyway
                } else {
                    showError('Failed to discard contract analysis. Please try again.');
                    return; // Don't close modal if discard failed
                }
            }
        }
        
        setFormData({
            category: resolvedInitialCategory,
            product_type: initialProductType || '',
            subtype_label: '',
            country_of_origin: '',
            supplier_crm_profile: '',
            source_location: '',
            gl_account_number_sales: '',
            gl_account_number_purchase: '',
            cost_center_code_sales: '',
            cost_center_code_purchase: '',
            cost_category_sales: '',
            cost_category_purchase: '',
        });
        setErrors({});
        resetDuplicateCheck();
        // Reset CRM autocomplete state
        setShowCrmDropdown(false);
        setCrmSearchTerm('');
        setFilteredCrmPartners([]);
        setSelectedCrmPartnerName('');
        setCreatingProfile(false);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content stock-form-modal">
                <div className="modal-header">
                    <h2>
                        <span className="modal-icon-badge">
                            <FontAwesomeIcon icon={faBox} />
                        </span>
                        Add New Product Item
                    </h2>
                    <button type="button" 
                        className="close-button"
                        onClick={handleClose}
                        disabled={loading}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                <form onSubmit={handleSubmit} className="stock-form">
                    {errors.general && (
                        <div className="error-message">
                            {errors.general}
                        </div>
                    )}

                    {/* "You may already have this product." Advisory only -- it
                      * names what exists, offers the one-click way to use it,
                      * and always leaves "Create it anyway" open. Entering the
                      * same product twice is what splits its trading history
                      * across two rows. */}
                    {duplicateCheck.status === 'matched' && (
                        <div
                            role="alert"
                            className="stock-duplicate-warning"
                            style={{
                                border: '1px solid #f59e0b',
                                background: '#fffbeb',
                                borderRadius: '8px',
                                padding: '14px 16px',
                                marginBottom: '16px',
                            }}
                        >
                            <div style={{ fontWeight: 600, color: '#92400e', marginBottom: '8px' }}>
                                You may already have this product
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#92400e', marginBottom: '10px' }}>
                                Creating it again splits its history across two rows.
                            </div>
                            {duplicateCheck.matches.map((match) => (
                                <div
                                    key={match.id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '12px',
                                        padding: '8px 0',
                                        borderTop: '1px solid #fde68a',
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600, color: '#111827' }}>
                                            {match.display_name}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                            {match.category_display}
                                            {' — '}
                                            {match.variants_count === 1
                                                ? '1 item'
                                                : `${match.variants_count} items`}
                                        </div>
                                    </div>
                                    {onUseExisting && (
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => onUseExisting(match)}
                                            disabled={loading}
                                        >
                                            Use this one instead
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* A check that could not run is not an all-clear. It says
                      * so, and it does not stop the product being created. */}
                    {duplicateCheck.status === 'failed' && (
                        <div
                            role="status"
                            className="stock-duplicate-check-failed"
                            style={{
                                border: '1px solid #d1d5db',
                                background: '#f9fafb',
                                borderRadius: '8px',
                                padding: '10px 14px',
                                marginBottom: '16px',
                                fontSize: '0.8rem',
                                color: '#4b5563',
                            }}
                        >
                            We could not check whether this product already exists. You can
                            still create it — please check the product list afterwards.
                        </div>
                    )}


                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="category">Category *</label>
                            <select
                                id="category"
                                name="category"
                                value={formData.category}
                                onChange={handleCategoryChange}
                                className={`form-select ${errors.category ? 'error' : ''}`}
                                disabled={loading}
                            >
                                {CATEGORIES.map((cat) => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                            {errors.category && (
                                <span className="error-text">{errors.category}</span>
                            )}
                        </div>

                        {/* HMS asked for Lubrication with no sub-categories, so the
                          * branded categories show brand + product name here instead
                          * of a subtype the user would have to invent. */}
                        {isBranded ? (
                            <div className="form-group">
                                <label htmlFor="supplier_brand">Supplier Brand *</label>
                                <input
                                    type="text"
                                    id="supplier_brand"
                                    name="supplier_brand"
                                    value={formData.supplier_brand}
                                    onChange={handleChange}
                                    onBlur={handleIdentityBlur}
                                    className={`form-input ${errors.supplier_brand ? 'error' : ''}`}
                                    placeholder="e.g. SHELL, TOTAL LUBMARINE"
                                    maxLength={120}
                                    disabled={loading}
                                />
                                {errors.supplier_brand && (
                                    <span className="error-text">{errors.supplier_brand}</span>
                                )}
                            </div>
                        ) : (
                        <div className="form-group">
                            <label htmlFor="product_type">Subtype *</label>
                            {isOtherCategory ? (
                                <input
                                    type="text"
                                    id="subtype_label"
                                    name="subtype_label"
                                    value={formData.subtype_label}
                                    onChange={handleChange}
                                    onBlur={(e) => {
                                        const normalized = normalizeSubtypeLabel(e.target.value);
                                        setFormData((prev) => ({ ...prev, subtype_label: normalized }));
                                        runDuplicateCheck({ ...formData, subtype_label: normalized });
                                    }}
                                    maxLength={SUBTYPE_LABEL_MAX_LENGTH}
                                    className={`form-input ${errors.subtype_label ? 'error' : ''}`}
                                    placeholder={'Describe the product or service (e.g. "Freight", "Inspection", "Custom Compound")'}
                                    disabled={loading}
                                />
                            ) : (
                                <select
                                    id="product_type"
                                    name="product_type"
                                    value={formData.product_type}
                                    onChange={(e) => {
                                        handleChange(e);
                                        runDuplicateCheck({ ...formData, product_type: e.target.value });
                                    }}
                                    className={`form-select ${errors.product_type ? 'error' : ''}`}
                                    disabled={loading}
                                >
                                    <option value="">Select a subtype...</option>
                                    {subtypeOptions.map((sub) => (
                                        <option key={sub.value} value={sub.value}>
                                            {sub.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                            {(errors.product_type || errors.subtype_label) && (
                                <span className="error-text">
                                    {errors.subtype_label || errors.product_type}
                                </span>
                            )}
                            {isOtherCategory && !errors.subtype_label && (
                                <small style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                                    Free text. Max {SUBTYPE_LABEL_MAX_LENGTH} characters. Use this for services or anything that doesn't fit the standard categories.
                                </small>
                            )}
                        </div>
                        )}
                    </div>

                    {/* The rest of a branded product's identity. Brand + name are what
                      * make two lube rows different products; the code is a supplier
                      * reference that several products may legitimately share. */}
                    {isBranded && (
                        <>
                            <div className="form-group">
                                <label htmlFor="product_name">Product Name *</label>
                                <input
                                    type="text"
                                    id="product_name"
                                    name="product_name"
                                    value={formData.product_name}
                                    onChange={handleChange}
                                    onBlur={handleIdentityBlur}
                                    className={`form-input ${errors.product_name ? 'error' : ''}`}
                                    placeholder="e.g. SHELL ALEXIA 50"
                                    maxLength={200}
                                    disabled={loading}
                                />
                                {errors.product_name && (
                                    <span className="error-text">{errors.product_name}</span>
                                )}
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="product_code">Product Code</label>
                                    <input
                                        type="text"
                                        id="product_code"
                                        name="product_code"
                                        value={formData.product_code}
                                        onChange={handleChange}
                                        onBlur={handleIdentityBlur}
                                        className="form-input"
                                        placeholder="e.g. SMCYL570"
                                        maxLength={64}
                                        disabled={loading}
                                    />
                                    <small style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                                        Supplier's reference. Several products may share one code.
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="product_description">Description</label>
                                    <input
                                        type="text"
                                        id="product_description"
                                        name="product_description"
                                        value={formData.product_description}
                                        onChange={handleChange}
                                        className="form-input"
                                        placeholder="e.g. CYLINDER OIL SAE 50 BN 70"
                                        maxLength={255}
                                        disabled={loading}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    {!isBranded && (
                    <>
                    <div className="form-group">
                        <label htmlFor="country_of_origin">Country of Origin</label>
                        <select
                            id="country_of_origin"
                            name="country_of_origin"
                            value={formData.country_of_origin}
                            onChange={handleChange}
                            onBlur={handleIdentityBlur}
                            className={`form-select ${errors.country_of_origin ? 'error' : ''}`}
                            disabled={loading}
                        >
                            <option value="">Select a country...</option>
                            {countries.map(country => (
                                <option key={country.value} value={country.value}>
                                    {country.label}
                                </option>
                            ))}
                        </select>
                        {errors.country_of_origin && (
                            <span className="error-text">{errors.country_of_origin}</span>
                        )}
                    </div>
                    
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="supplier_crm_profile">Supplier (CRM Profile)</label>
                            
                            <div className="crm-modern-multi-select-container" ref={crmDropdownRef}>
                                <div 
                                    className={`crm-modern-multi-select-header ${showCrmDropdown ? 'active' : ''} ${errors.supplier_crm_profile ? 'error' : ''}`}
                                    onClick={() => {
                                        if (!loading) {
                                            setShowCrmDropdown(!showCrmDropdown);
                                            // Focus search input when opening dropdown
                                            if (!showCrmDropdown) {
                                                setTimeout(() => {
                                                    if (crmSearchInputRef.current) {
                                                        crmSearchInputRef.current.focus();
                                                    }
                                                }, 150);
                                            }
                                        }
                                    }}
                                >
                                    <span>
                                        {loadingCrmPartners ? 
                                            'Loading CRM partners...' :
                                            selectedCrmPartnerName || 'Click to search and select a CRM supplier partner'}
                                    </span>
                                </div>
                                
                                {showCrmDropdown && (
                                    <div className="crm-modern-multi-select-dropdown">
                                        {/* Search Input */}
                                        <div className="crm-search-input-container">
                                            <input
                                                ref={crmSearchInputRef}
                                                type="text"
                                                className="crm-search-input"
                                                placeholder="Type to search CRM partner names..."
                                                value={crmSearchTerm}
                                                onChange={(e) => setCrmSearchTerm(e.target.value)}
                                                autoFocus
                                            />
                                        </div>

                                        <div className="crm-modern-multi-select-options">
                                            {!debouncedCrmSearchTerm.trim() ? (
                                                <div className="crm-modern-multi-select-option">
                                                    <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                        Start typing to search for CRM partners...
                                                    </span>
                                                </div>
                                            ) : filteredCrmPartners.length > 0 ? (
                                                filteredCrmPartners.map((partner) => (
                                                    <div 
                                                        key={partner.id} 
                                                        className="crm-modern-multi-select-option"
                                                        onClick={() => handleCrmPartnerSelect(partner)}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <span>{partner.name}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="crm-modern-multi-select-option no-results-option">
                                                    <div className="no-results-content">
                                                        <span style={{ fontStyle: 'italic', color: '#666' }}>
                                                            No CRM partners found matching "{debouncedCrmSearchTerm}"
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-primary add-profile-btn"
                                                            onClick={handleCreateCrmProfile}
                                                            disabled={creatingProfile || !debouncedCrmSearchTerm.trim()}
                                                            title={`Create new CRM profile for "${debouncedCrmSearchTerm}"`}
                                                        >
                                                            {creatingProfile ? (
                                                                <>
                                                                    <FontAwesomeIcon icon={faSpinner} spin />
                                                                    Creating...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <FontAwesomeIcon icon={faPlus} />
                                                                    Add Profile
                                                                </>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {errors.supplier_crm_profile && (
                                <span className="error-text">{errors.supplier_crm_profile}</span>
                            )}
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="source_location">Source Location</label>
                            <input
                                type="text"
                                id="source_location"
                                name="source_location"
                                value={formData.source_location}
                                onChange={handleChange}
                                onBlur={handleIdentityBlur}
                                className={`form-input ${errors.source_location ? 'error' : ''}`}
                                placeholder="e.g., Hunter Valley, Rotterdam Terminal, Shell Refinery..."
                                disabled={loading}
                            />
                            {errors.source_location && (
                                <span className="error-text">{errors.source_location}</span>
                            )}
                        </div>
                    </div>
                    </>
                    )}

                    {/* Finance coding — required per leg. Products store the
                        GROUP-CHART number/code; invoices resolve it against the
                        invoicing entity's chart automatically.

                        Optional on branded products: a supplier lube catalogue
                        arrives uncoded and finance codes it afterwards. The
                        Products tab shows how many are still waiting so the gap
                        does not become invisible. */}
                    <div className="form-row">
                        <div className={`form-group ${errors.gl_account_number_sales ? 'invoice-field-has-error' : ''}`}>
                            <label htmlFor="gl_account_number_sales">GL Account — Sales (revenue) {isBranded ? '' : '*'}</label>
                            <SearchableSelect
                                id="gl_account_number_sales"
                                fetchUrl="/api/invoicing/gl-accounts/"
                                valueKey="account_number"
                                displayFn={(gl) => `${gl.account_number} - ${gl.account_name}`}
                                value={formData.gl_account_number_sales}
                                onChange={handleAccountingChange('gl_account_number_sales')}
                                placeholder="Search GL account..."
                                disabled={loading}
                            />
                            {errors.gl_account_number_sales && <span className="error-text">{errors.gl_account_number_sales}</span>}
                        </div>
                        <div className={`form-group ${errors.gl_account_number_purchase ? 'invoice-field-has-error' : ''}`}>
                            <label htmlFor="gl_account_number_purchase">GL Account — Purchase (CoS) {isBranded ? '' : '*'}</label>
                            <SearchableSelect
                                id="gl_account_number_purchase"
                                fetchUrl="/api/invoicing/gl-accounts/"
                                valueKey="account_number"
                                displayFn={(gl) => `${gl.account_number} - ${gl.account_name}`}
                                value={formData.gl_account_number_purchase}
                                onChange={handleAccountingChange('gl_account_number_purchase')}
                                placeholder="Search GL account..."
                                disabled={loading}
                            />
                            {errors.gl_account_number_purchase && <span className="error-text">{errors.gl_account_number_purchase}</span>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className={`form-group ${errors.cost_center_code_sales ? 'invoice-field-has-error' : ''}`}>
                            <label htmlFor="cost_center_code_sales">Cost Centre — Sales</label>
                            <SearchableSelect
                                id="cost_center_code_sales"
                                fetchUrl="/api/invoicing/cost-centers/"
                                valueKey="cost_center_code"
                                displayFn={costCentreLabel}
                                value={formData.cost_center_code_sales}
                                onChange={handleAccountingChange('cost_center_code_sales')}
                                placeholder="Search cost centre..."
                                disabled={loading}
                            />
                            {errors.cost_center_code_sales && <span className="error-text">{errors.cost_center_code_sales}</span>}
                        </div>
                        <div className={`form-group ${errors.cost_center_code_purchase ? 'invoice-field-has-error' : ''}`}>
                            <label htmlFor="cost_center_code_purchase">Cost Centre — Purchase</label>
                            <SearchableSelect
                                id="cost_center_code_purchase"
                                fetchUrl="/api/invoicing/cost-centers/"
                                valueKey="cost_center_code"
                                displayFn={costCentreLabel}
                                value={formData.cost_center_code_purchase}
                                onChange={handleAccountingChange('cost_center_code_purchase')}
                                placeholder="Search cost centre..."
                                disabled={loading}
                            />
                            {errors.cost_center_code_purchase && <span className="error-text">{errors.cost_center_code_purchase}</span>}
                        </div>
                    </div>

                    <div className="form-row">
                        <div className={`form-group ${errors.cost_category_sales ? 'invoice-field-has-error' : ''}`}>
                            <label htmlFor="cost_category_sales">Cost Category — Sales</label>
                            <SearchableSelect
                                id="cost_category_sales"
                                fetchUrl="/api/invoicing/cost-categories/"
                                displayFn={(cc) => `${cc.code} - ${cc.short_name}`}
                                value={formData.cost_category_sales}
                                onChange={handleAccountingChange('cost_category_sales')}
                                placeholder="Search cost category..."
                                disabled={loading}
                            />
                            {errors.cost_category_sales && <span className="error-text">{errors.cost_category_sales}</span>}
                        </div>
                        <div className={`form-group ${errors.cost_category_purchase ? 'invoice-field-has-error' : ''}`}>
                            <label htmlFor="cost_category_purchase">Cost Category — Purchase</label>
                            <SearchableSelect
                                id="cost_category_purchase"
                                fetchUrl="/api/invoicing/cost-categories/"
                                displayFn={(cc) => `${cc.code} - ${cc.short_name}`}
                                value={formData.cost_category_purchase}
                                onChange={handleAccountingChange('cost_category_purchase')}
                                placeholder="Search cost category..."
                                disabled={loading}
                            />
                            {errors.cost_category_purchase && <span className="error-text">{errors.cost_category_purchase}</span>}
                        </div>
                    </div>
                    <small style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem', display: 'block' }}>
                        Finance coding uses the group chart: pick once per leg, and every
                        invoice books against the issuing entity's chart automatically.
                    </small>
                    
                    <div className="help-text">
                        <p>* Required fields</p>
                        <p>Either supplier or source location must be specified.</p>
                    </div>
                    
                    <div className="modal-actions centered">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading || duplicateCheck.status === 'checking'}
                        >
                            {loading || duplicateCheck.status === 'checking' ? (
                                <>
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                    {duplicateCheck.status === 'checking' ? 'Checking...' : 'Creating...'}
                                </>
                            ) : (
                                <>
                                    <FontAwesomeIcon icon={faSave} />
                                    {duplicateCheck.status === 'matched' && overrideDuplicates
                                        ? 'Create it anyway'
                                        : 'Add Item'}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default StockItemForm; 